import React from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm, propTypes, SubmissionError } from 'redux-form';
import difflib from 'jsdifflib';
import ReCAPTCHA from 'react-google-recaptcha';

import { required, email as isValidEmail } from '../../ReduxForm/validation';
import {
  ReduxFormField,
  ReduxFormError,
  ReduxFormSubmitButton,
} from '../Forms/index';
import { noop } from '../../../utilities/functions';
import { TOS_GENERAL_URL, PRIVACY_POLICY_URL } from '../../../../constants';

import './styles.scss';

/* global _RECAPTCHA_KEY */

const hasPassword = (value) =>
  value
    ? null
    : 'Password cannot be empty. Please enter a password between 8 to 64 characters long.';

const minPasswordLength = (value) =>
  value && value.length < 8
    ? 'Your password must be at least 8 characters long.'
    : null;

const maxPasswordLength = (value) =>
  value && value.length > 64
    ? 'Your password must be no more than 64 characters long.'
    : null;

const PASSWORD_COMMON_THRESHOLD = 4;

export const longestCommonSubstringLength = (stringA, stringB) => {
  const sA = stringA || '';
  const sB = stringB || '';
  const sequenceMatcher = new difflib.SequenceMatcher(sA, sB, null);
  // gives an array [string1 starting index, string2 starting index, length of common sequence]
  const lcs = sequenceMatcher.find_longest_match(0, sA.length, 0, sB.length);
  return lcs[2];
};

export const noEmailInPassword = (value, { emailAddress }) => {
  const lcsLength = longestCommonSubstringLength(value, emailAddress);
  return lcsLength > PASSWORD_COMMON_THRESHOLD
    ? 'Your password must not contain more than 4 characters common in sequence from your email address.'
    : null;
};

export const noFullNameInPassword = (value, { fullName }) => {
  const lcsLength = longestCommonSubstringLength(value, fullName);
  return lcsLength > PASSWORD_COMMON_THRESHOLD
    ? 'Your password must not contain more than 4 characters common in sequence from your full name.'
    : null;
};

export const primaryCharsInPassword = (value) => {
  // Positive Lookahead (?=.*\d): at least one digital
  // Positive Lookahead (?=.*[a-z]): at least one lower case letter
  // Positive Lookahead (?=.*[A-Z]): at least one upper case letter
  // Positive Lookahead (?=.*[^a-zA-Z0-9]): at least one special character
  // Negative Lookahead (?!.*\s): no space
  // .* matches any character (except for line terminators)
  const passwPettern = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).*$/;
  return value.match(passwPettern)
    ? null
    : 'Your password must contain at least one lowercase letter, one uppercase letter, one numeric digit, and one special character';
};

const acceptTerms = (value) =>
  value ? undefined : 'Your consent is required to continue';

class _SignUpForm extends React.PureComponent {
  static propTypes = {
    ...propTypes,
    onSignUp: PropTypes.func.isRequired,
  };

  state = {
    isCaptchaValidated: false,
    recaptchaToken: null,
  };

  recaptchaRef = React.createRef();

  componentDidUpdate(prevProps) {
    const { invalid } = this.props;
    if (invalid && invalid !== prevProps.invalid) {
      this.resetCaptcha();
    }
  }

  onSubmit = ({ emailAddress, password, fullName, subscribe }) => {
    const { onSignUp } = this.props;
    const { recaptchaToken } = this.state;

    return onSignUp(
      emailAddress,
      password,
      fullName,
      subscribe,
      recaptchaToken
    ).then(noop, (errors) => {
      this.resetCaptcha();
      throw new SubmissionError(errors);
    });
  };

  resetCaptcha = () => {
    this.setState({ isCaptchaValidated: false, recaptchaToken: null });
    if (this.recaptchaRef && this.recaptchaRef.current) {
      this.recaptchaRef.current.reset();
    }
  };

  handleCaptchaSubmit = (value) => {
    this.setState({
      isCaptchaValidated: value !== null,
      recaptchaToken: value,
    });
  };

  render() {
    const { handleSubmit, submitting, error } = this.props;
    const { isCaptchaValidated } = this.state;
    const canSubmit = !error && isCaptchaValidated;

    return (
      <div className="sign-up-form">
        <form onSubmit={handleSubmit(this.onSubmit)}>
          <Field
            name="emailAddress"
            type="text"
            label="Email"
            component={ReduxFormField}
            validate={[required, isValidEmail]}
          />
          <Field
            name="password"
            type="password"
            label="Password"
            component={ReduxFormField}
            validate={[
              hasPassword,
              minPasswordLength,
              maxPasswordLength,
              noEmailInPassword,
              noFullNameInPassword,
              primaryCharsInPassword,
            ]}
          />
          <Field
            name="fullName"
            type="text"
            label="Full name"
            component={ReduxFormField}
            validate={required}
          />
          <Field
            type="checkbox"
            name="terms"
            label={
              <span>
                I agree to the{' '}
                <a href={TOS_GENERAL_URL} target="_blank" rel="noreferrer">
                  Terms of service
                </a>{' '}
                and{' '}
                <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
                  Privacy policy
                </a>
              </span>
            }
            component={ReduxFormField}
            validate={acceptTerms}
          />
          <Field
            type="checkbox"
            name="subscribe"
            label="Subscribe to our newsletter"
            component={ReduxFormField}
          />

          {error && <ReduxFormError error={error} />}

          <div className="recaptcha-container">
            <ReCAPTCHA
              ref={this.recaptchaRef}
              sitekey={_RECAPTCHA_KEY}
              onChange={this.handleCaptchaSubmit}
            />
          </div>

          <ReduxFormSubmitButton
            label="Get started"
            {...{ submitting }}
            disabled={!canSubmit}
          />
        </form>
      </div>
    );
  }
}

export const SignUpForm = reduxForm({
  form: 'signUp',
})(_SignUpForm);

export const ModalSignUpForm = reduxForm({
  form: 'modalSignUp',
})(_SignUpForm);
